// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type StockAnalysis } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import {
  CountryChart,
  DonutChartContainer,
  FactorsChart,
  KpiBoxes,
  type KpiBoxesProps,
  SectorComposition,
  SegmentMatrix,
} from '@shared/ui-kit/components';
import { KeyMetricsTable } from '@shared/ui-kit/features/overview';
import {
  formatToMoney,
  formatToNumber,
  formatToPercent,
} from '@shared/ui-kit/utils';
import { type FC, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export interface OverviewStocksProps {
  analysis: StockAnalysis;
  currency: string;
}

export const OverviewStocks: FC<OverviewStocksProps> = ({ analysis, currency }) => {
  const { t } = useTranslation();
  const outstandingBalanceTotal = analysis.capital_gains.outstanding_balance;
  /* istanbul ignore next */
  const kpiData: KpiBoxesProps[] = [
    {
      text: t('common.investment', { ns: 'common' }),
      value: formatToMoney(Math.trunc(outstandingBalanceTotal), currency),
      numberPositions: analysis.concentration.length,
      icon: 'TrendingUp',
      color: 'success',
    },
    {
      text: t('glossary.avg-market-cap', { ns: 'glossary' }),
      value: formatToMoney(
        Math.trunc(analysis?.fundamentals?.avg_market_cap),
        currency,
      ),
      popupName: 'kpis.avgMarketCap',
      icon: 'ChartColumn',
      color: 'main',
    },
    {
      text: t('glossary.price-per-earnings', { ns: 'glossary' }),
      value: formatToNumber(analysis?.fundamentals?.pe),
      popupName: 'kpis.pricePerEarnings',
      icon: 'Percent',
      color: 'secondary',
    },
    {
      text: t('glossary.dividend-yield', { ns: 'glossary' }),
      value: formatToPercent(analysis?.fundamentals?.dividend_yield),
      popupName: 'kpis.dividendYield',
      icon: 'DollarSign',
      color: 'success',
    },
  ];

  const categories = useMemo(
    () => analysis.sectors.map(sector => sector.exposure),
    [analysis.sectors],
  );

  const percentages = useMemo(
    () => analysis.sectors.map(sector => sector.weight),
    [analysis.sectors],
  );

  const benchmarks = useMemo(
    () => analysis.sectors.map(sector => sector.weight_benchmark),
    [analysis.sectors],
  );

  const currencyName = useMemo(
    () => analysis.currencies.map(category => category.exposure),
    [analysis.currencies],
  );

  const currencyWeight = useMemo(
    () => analysis.currencies.map(category => category.weight),
    [analysis.currencies],
  );

  const countries = useMemo(() => {
    return analysis.countries.reduce(
      (acc: { labels: string[]; values: number[] }, country) => {
        const { exposure, weight } = country;
        acc.labels.push(exposure);
        acc.values.push(weight);
        return acc;
      },
      { labels: [], values: [] },
    );
  }, [analysis.countries]);

  return (
    <>
      <div className="mt-2 grid grid-cols-4 gap-6">
        {kpiData.map((vals, idx) => (
          <KpiBoxes key={idx} {...vals} />
        ))}
      </div>
      <div className="risk-return-grid">
        <KeyMetricsTable analysis={analysis} />
        <SectorComposition
          categories={categories}
          percentages={percentages}
          benchmarks={benchmarks}
        />
        <SegmentMatrix segmentMatrix={analysis!.segment_matrix} />
        <FactorsChart factors={analysis.factors} />
        <DonutChartContainer
          name={<Trans i18nKey="common.currency" ns="common" />}
          exposure={currencyName}
          weight={currencyWeight}
        />
        <CountryChart countries={countries.labels} percentages={countries.values} />
      </div>
    </>
  );
};
